import {
  Component,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { CampanhaStatus } from '../../core/enum/campanha-status.enum';
import { TipoCampanhaBack } from '../../core/enum/tipo-campanha-back.enum';
import { podeEditarCampanha, podeExcluirCampanha } from '../../core/utils/campanha-permissions.util';
import { CampanhaService } from '../../core/services/campanha.service';
import { ToastService } from '../../core/services/toast.service';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { LoaderService } from '../../shared/services/loader/loader.service';
import { Campanha, CampanhaFiltros } from '../../core/models/campanha.model';
import { TipoCampanha } from '../../core/models/tipo-campanha.model';
import { formatDateIsoToBr } from '../../core/utils/date-format.util';
import { DataTableComponent } from '../../shared/components/data-table/data-table.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import {
  FilterBarComponent,
  FilterField,
} from '../../shared/components/filter-bar/filter-bar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import {
  ActionsMenuPosition,
  adjustActionsMenuPosition,
  getActionsMenuPosition,
} from '../../shared/utils/actions-menu.util';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-campanhas-list',
  imports: [
    RouterLink,
    PageHeaderComponent,
    FilterBarComponent,
    DataTableComponent,
    StatusBadgeComponent,
    PaginationComponent,
    EmptyStateComponent,
    AlertDialogComponent,
  ],
  templateUrl: './campanhas-list.component.html',
  styleUrl: './campanhas-list.component.scss',
})
export class CampanhasListComponent {
  private readonly campanhaService = inject(CampanhaService);
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly injector = inject(Injector);

  private readonly campanhasSignal = signal<readonly Campanha[]>([]);

  readonly campanhas = this.campanhasSignal.asReadonly();
  readonly currentPage = signal(1);
  readonly menuAbertoId = signal<string | null>(null);
  readonly menuPosition = signal<ActionsMenuPosition | null>(null);
  readonly showExcluirId = signal<string | null>(null);
  readonly relatoriosMenuAberto = signal(false);
  readonly pageSize = PAGE_SIZE;
  readonly formatDate = formatDateIsoToBr;

  private readonly tiposCampanhaSignal = signal<readonly TipoCampanha[]>([]);

  /** Filter-bar field descriptors for the campanhas list; "Tipo" options vêm de GET /v1/Campanha/TiposCampanhas. */
  readonly filterFields = computed<FilterField[]>(() => [
    {
      key: 'nome',
      label: 'Procure por nome',
      type: 'text',
      placeholder: 'Digite o nome da campanha',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      defaultValue: CampanhaStatus.AtivoPortal,
      clearValue: 'todos',
      options: [
        { value: 'todos', label: 'Todos' },
        { value: CampanhaStatus.Rascunho, label: 'Rascunho' },
        { value: CampanhaStatus.Agendada, label: 'Agendada' },
        { value: CampanhaStatus.AtivoPortal, label: 'Ativa Portal' },
        { value: CampanhaStatus.Encerrada, label: 'Encerrada' },
        { value: CampanhaStatus.Cancelada, label: 'Cancelada' },
      ],
    },
    {
      key: 'tipo',
      label: 'Tipo',
      type: 'select',
      defaultValue: 'todos',
      options: [
        { value: 'todos', label: 'Todos' },
        ...this.tiposCampanhaSignal()
          .filter((tipo) => tipo.codigo === TipoCampanhaBack.Suitability)
          .map((tipo) => ({
            value: tipo.descricao,
            label: tipo.descricao,
          })),
      ],
    },
    {
      key: 'periodoDe',
      label: 'Período',
      type: 'daterange',
      keyTo: 'periodoAte',
    },
  ]);

  constructor() {
    this.campanhaService.listarTiposCampanha().subscribe({
      next: (tipos) => this.tiposCampanhaSignal.set(tipos),
      error: () => this.tiposCampanhaSignal.set([]),
    });
  }

  readonly campanhasPaginadas = computed(() => {
    const inicio = (this.currentPage() - 1) * PAGE_SIZE;
    return this.campanhasSignal().slice(inicio, inicio + PAGE_SIZE);
  });

  /**
   * Called by `app-filter-bar` on submit and on init.
   * Maps the generic record to `CampanhaFiltros` replicating the previous `emitFilters` logic.
   */
  onFiltersChange(record: Record<string, string>): void {
    this.currentPage.set(1);
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);

    const filtros: CampanhaFiltros = { status: record['status'] };

    const nome = (record['nome'] ?? '').trim();
    if (nome) {
      filtros.nome = nome;
    }

    if (record['tipo'] && record['tipo'] !== 'todos') {
      filtros.tipo = record['tipo'];
    }

    if (record['periodoDe']) {
      filtros.periodoDe = record['periodoDe'];
    }

    if (record['periodoAte']) {
      filtros.periodoAte = record['periodoAte'];
    }

    this.listarCampanhas(filtros);
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);
  }

  toggleRelatoriosMenu(event: Event): void {
    event.stopPropagation();
    this.relatoriosMenuAberto.update((aberto) => !aberto);
  }

  onRelatorioOption(): void {
    this.relatoriosMenuAberto.set(false);
    this.toastService.show('Relatório disponível em versão futura.', 'info');
  }

  voltar(): void {
    void this.router.navigate(['/']);
  }

  onCriarCampanha(): void {
    void this.router.navigate(['/campanhas/nova']);
  }

  toggleMenu(id: string, event: Event): void {
    event.stopPropagation();

    if (this.menuAbertoId() === id) {
      this.menuAbertoId.set(null);
      this.menuPosition.set(null);
      return;
    }

    this.menuAbertoId.set(id);
    this.menuPosition.set(getActionsMenuPosition(event));
    this.adjustMenuPosition();
  }

  private adjustMenuPosition(): void {
    afterNextRender(
      () => {
        const adjusted = adjustActionsMenuPosition(this.menuPosition());
        if (adjusted) {
          this.menuPosition.set(adjusted);
        }
      },
      { injector: this.injector },
    );
  }

  irParaDetalhe(id: string): void {
    this.menuAbertoId.set(null);
    void this.router.navigate(['/campanhas', id]);
  }

  onVisualizar(campanha: Campanha, event: Event): void {
    event.stopPropagation();
    this.menuAbertoId.set(null);

    if (!podeEditarCampanha(campanha.status)) {
      this.irParaVisualizacao(campanha.id);
      return;
    }

    this.irParaDetalhe(campanha.id);
  }

  irParaVisualizacao(id: string): void {
    this.menuAbertoId.set(null);
    void this.router.navigate(['/campanhas', id, 'editar'], {
      queryParams: { modo: 'visualizar' },
    });
  }

  irParaEditar(id: string): void {
    this.menuAbertoId.set(null);
    void this.router.navigate(['/campanhas', id, 'editar']);
  }

  /** RN001 (BUSINESS_RULES.MD): só campanha Rascunho ou Agendada pode ser editada. */
  podeEditar(campanha: Campanha): boolean {
    return podeEditarCampanha(campanha.status);
  }

  /** Espelha CampanhaVigenciaGuard.GarantirNaoVigenteEAtiva: só Rascunho ou Agendada aceita exclusão. */
  podeExcluir(campanha: Campanha): boolean {
    return podeExcluirCampanha(campanha.status);
  }

  // Encerrar/Pausar/Cancelar removidos do menu (decisão do PO, 2026-08-10):
  // CampanhaVigenciaGuard.GarantirNaoVigenteEAtiva recusa qualquer PATCH/PUT/DELETE fora de
  // Rascunho ou Agendada; as três ações só fariam sentido saindo de Ativa (Portal), que é
  // justamente o status congelado, e não existe endpoint dedicado para nenhuma delas hoje.
  // onAcaoFutura(event: Event): void {
  //   event.stopPropagation();
  //   this.menuAbertoId.set(null);
  //   this.toastService.show('Ação disponível em uma versão futura.', 'info');
  // }

  onClonar(campanha: Campanha, event: Event): void {
    event.stopPropagation();
    this.menuAbertoId.set(null);

    this.campanhaService.prepararCampanhaClonada(campanha.id).subscribe({
      next: (campanhaClonada) => {
        if (!campanhaClonada) {
          this.toastService.show('Não foi possível clonar a campanha.', 'error');
          return;
        }

        void this.router.navigateByUrl('/campanhas/nova', { state: { campanhaClonada } });
      },
      error: (error: unknown) => {
        const message =
          error instanceof ApiHttpError ? error.message : 'Não foi possível clonar a campanha.';
        this.toastService.show(message, 'error');
      },
    });
  }

  onExcluirClick(campanha: Campanha, event: Event): void {
    event.stopPropagation();
    this.menuAbertoId.set(null);
    this.showExcluirId.set(campanha.id);
  }

  cancelarExclusao(): void {
    this.showExcluirId.set(null);
  }

  confirmarExclusao(): void {
    const id = this.showExcluirId();
    if (!id) {
      return;
    }

    this.showExcluirId.set(null);

    this.campanhaService.excluirCampanha(id).subscribe({
      next: (excluida) => {
        if (!excluida) {
          this.toastService.show('Não foi possível excluir a campanha.', 'error');
          return;
        }

        this.campanhasSignal.update((lista) => lista.filter((item) => item.id !== id));
        this.toastService.show('Campanha excluída com sucesso.', 'success');
      },
      error: (error: unknown) => {
        const message =
          error instanceof ApiHttpError ? error.message : 'Não foi possível excluir a campanha.';
        this.toastService.show(message, 'error');
      },
    });
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);
    this.relatoriosMenuAberto.set(false);
  }

  private listarCampanhas(filtros: CampanhaFiltros): void {
    this.loaderService.showLoader();

    this.campanhaService
      .listarCampanhas(filtros)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe({
        next: (lista) => this.campanhasSignal.set(lista),
        error: (error: unknown) => {
          this.campanhasSignal.set([]);
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível carregar as campanhas.';
          this.toastService.show(message, 'error');
        },
      });
  }
}
